import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "../components/others/Sidebar";
import { HelpCircle, MenuIcon, Search, User } from "lucide-react";
import { Dropdown, Menu } from "antd";
import useAuth from "../hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import CustomInput from "../shared/CustomInput";
import CustomSelect from "../shared/CustomSelect";
import CustomArea from "../shared/CustomArea";
import { addSupportTicket } from "../sdk/support";
import { useMutation } from "react-query";
import { useError } from "../context/ErrorProvider";
import Loading from "../shared/Loading";
import TicketSuccess from "../components/ticket/TicketSuccess";

const HomeLayout = () => {
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const [showHelp, setShowHelp] = useState(false);
  const [errors, setErrors] = useState({});
  const { showError } = useError();
  const [openTicketSuccess, setOpenTicketSuccess] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    impactedPlatform: "",
    deviceOrBrowser: "",
    stepsToReplicate: "",
    expectedResult: "",
    actualResult: "",
    addMoreInfo: "",
    usersImpacted: "",
    memberId: "",
    replicated: "",
    date: "",
    time: "",
    files: null,
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
    } else if (!/^\+?\d{7,15}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid phone number";
    }
    if (!formData.stepsToReplicate.trim()) {
      newErrors.stepsToReplicate = "Steps to replicate are required";
    }
    if (!formData.impactedPlatform.trim()) {
      newErrors.impactedPlatform = "Impacted platform is required";
    }
    if (!formData.deviceOrBrowser.trim()) {
      newErrors.deviceOrBrowser = "Device or browser is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, files: e.target.files }));
  };

  const submitSupportTicket = async () => {
    if (!validateForm()) return;
    mutate();
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["submit ticket"],
    mutationFn: () =>
      addSupportTicket(
        formData.fullName,
        formData.email,
        formData.phoneNumber,
        formData.impactedPlatform,
        formData.deviceOrBrowser,
        formData.stepsToReplicate,
        formData.expectedResult,
        formData.actualResult,
        formData.addMoreInfo,
        formData.usersImpacted,
        formData.memberId,
        formData.replicated,
        formData.date,
        formData.time,
        formData.files,
      ),
    onSuccess: () => {
      setOpenTicketSuccess(true);
      setShowHelp(false);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleMenuClick = ({ key }) => {
    if (key === "logout") {
      logout();
    } else if (key === "profile") {
      navigate("/dashboard/profile");
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="profile">Profile</Menu.Item>
      <Menu.Item key="logout">Logout</Menu.Item>
    </Menu>
  );
  return (
    <>
      {openTicketSuccess && (
        <TicketSuccess closeTicketSuccess={() => setOpenTicketSuccess(false)} />
      )}
      <div className="flex h-[100vh] w-[100vw] relative overflow-hidden">
        <div
          className={`h-full bg-white transition-all duration-300 ${
            sidebarCollapsed ? "w-[75px]" : "w-[17.5%]"
          }`}
        >
          <Sidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
        </div>
        <div
          className={`flex flex-col transition-all duration-300 pr-[25px] ${
            sidebarCollapsed ? "w-[calc(100%-75px)]" : "w-[82%]"
          }`}
        >
          <div
            className={`h-[75px] bg-[#F3F3F3] pr-[10px] mb-[10px] shadow-sm z-10 flex gap-[20px] items-center justify-between ${
              sidebarCollapsed ? "ml-[10px]" : "pl-[10px]"
            }`}
          >
            {!sidebarCollapsed ? (
              <MenuIcon
                className="cursor-pointer"
                onClick={toggleSidebar}
                size={40}
              />
            ) : (
              <div></div>
            )}
            <div className="flex items-center gap-[20px]">
              <div
                onClick={() => setShowHelp(true)}
                className="h-[42px] w-[42px] cursor-pointer rounded-[10px] border-[0.7px] border-[#E4E4E4] flex items-center justify-center"
              >
                <HelpCircle size={20} color="#222222" />
              </div>
              <Dropdown overlay={menu} trigger={["click"]}>
                {auth?.user?.profile_image ? (
                  <div className="h-[42px] w-[42px] rounded-full overflow-hidden border-[0.7px] border-[#E4E4E4] cursor-pointer">
                    <img
                      src={auth?.user?.profile_image}
                      alt="profile"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/default.png";
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-[42px] flex items-center justify-center w-[42px] rounded-full bg-white overflow-hidden border-[0.7px] border-[#E4E4E4] cursor-pointer">
                    <User />
                  </div>
                )}
              </Dropdown>
            </div>
          </div>
          <div
            className={`h-[calc(100vh-75px)] bg-[#F3F3F3] px-[10px] no-scrollbar overflow-y-auto ${
              sidebarCollapsed ? "ml-[10px]" : ""
            }`}
          >
            <Outlet />
          </div>
        </div>
        {/* Help Modal */}
        <AnimatePresence>
          {showHelp && (
            <>
              {/* Background overlay */}
              <motion.div
                className="fixed inset-0 bg-black/30 z-40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowHelp(false)}
              />

              {/* Sliding modal */}
              <motion.div
                className="fixed right-[0.5%] top-[1%] bottom-[1%] bg-white shadow-lg rounded-xl z-50 p-4 flex flex-col"
                style={{ width: 450 }}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
              >
                <div className="w-full h-full">
                  <div className="h-[10%] w-full">
                    <h2 className="text-lg font-semibold">Contact Support</h2>
                    <p className="text-sm text-[#737373] mb-[15px] text-[15px] font-light">
                      To help us investigate this issue, please provide the
                      below information.
                    </p>
                  </div>
                  <div className="h-[85%] overflow-y-auto w-full relative">
                    <div className="flex-1 overflow-y-auto">
                      <div>
                        <p className="text-[#000000] mb-[15px] font-medium">
                          General details
                        </p>
                        <div className="w-full mb-[15px] flex items-start justify-between">
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="name"
                              className="text-[14px] font-medium"
                            >
                              Full Name
                            </label>
                            <CustomInput
                              placeholder="Enter full name"
                              type="text"
                              value={formData.fullName}
                              onChange={(e) =>
                                handleChange("fullName", e.target.value)
                              }
                            />
                            {errors.fullName && (
                              <small className="text-red-500">
                                {errors.fullName}
                              </small>
                            )}
                          </div>
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="email"
                              className="text-[14px] font-medium"
                            >
                              Email
                            </label>
                            <CustomInput
                              placeholder="Enter email"
                              type="email"
                              value={formData.email}
                              onChange={(e) =>
                                handleChange("email", e.target.value)
                              }
                            />
                            {errors.email && (
                              <small className="text-red-500">
                                {errors.email}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="phone"
                            className="text-[14px] font-medium"
                          >
                            Phone Number
                          </label>
                          <CustomInput
                            placeholder="Enter phone number"
                            type="text"
                            value={formData.phoneNumber}
                            onChange={(e) =>
                              handleChange("phoneNumber", e.target.value)
                            }
                          />
                          {errors.phoneNumber && (
                            <small className="text-red-500">
                              {errors.phoneNumber}
                            </small>
                          )}
                        </div>

                        <div>
                          <p className="text-[#000000] mb-[15px] font-medium">
                            Platform details
                          </p>
                          <div className="w-full mb-[20px] flex items-start justify-between">
                            <div className="w-[49%] flex flex-col items-start">
                              <label
                                htmlFor="Impacted Platform"
                                className="text-[14px] font-medium"
                              >
                                Impacted Platform
                              </label>
                              <CustomSelect
                                name="platform"
                                options={[
                                  "Android",
                                  "iOS",
                                  "Web",
                                  "Admin Platform",
                                ]}
                                value={formData.impactedPlatform}
                                onChange={(e) =>
                                  handleChange(
                                    "impactedPlatform",
                                    e.target.value,
                                  )
                                }
                              />
                              {errors.impactedPlatform && (
                                <small className="text-red-500">
                                  {errors.impactedPlatform}
                                </small>
                              )}
                            </div>
                            <div className="w-[49%] flex flex-col items-start">
                              <label
                                htmlFor="Device or Browser"
                                className="text-[14px] font-medium"
                              >
                                Device or Browser affected
                              </label>
                              <CustomInput
                                placeholder="Enter device or browser affected"
                                type="text"
                                value={formData.deviceOrBrowser}
                                onChange={(e) =>
                                  handleChange(
                                    "deviceOrBrowser",
                                    e.target.value,
                                  )
                                }
                              />
                              {errors.deviceOrBrowser && (
                                <small className="text-red-500">
                                  {errors.deviceOrBrowser}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="text-[#000000] mb-[15px] font-medium">
                            Issues details
                          </p>
                          <div className="w-full mb-[15px] flex flex-col items-start">
                            <label
                              htmlFor="Steps"
                              className="text-[14px] font-medium"
                            >
                              Steps to Replicate
                            </label>
                            <CustomArea
                              placeholder="Enter steps to replicate"
                              value={formData.stepsToReplicate}
                              onChange={(e) =>
                                handleChange("stepsToReplicate", e.target.value)
                              }
                            />
                            {errors.stepsToReplicate && (
                              <small className="text-red-500">
                                {errors.stepsToReplicate}
                              </small>
                            )}
                          </div>
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="Expected"
                            className="text-[14px] font-medium"
                          >
                            Expected Result
                          </label>
                          <CustomArea
                            placeholder="Enter expected result"
                            value={formData.expectedResult}
                            onChange={(e) =>
                              handleChange("expectedResult", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="Actual"
                            className="text-[14px] font-medium"
                          >
                            Actual Result
                          </label>
                          <CustomArea
                            placeholder="Enter actual result"
                            value={formData.actualResult}
                            onChange={(e) =>
                              handleChange("actualResult", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="More Info"
                            className="text-[14px] font-medium"
                          >
                            Add More Information
                          </label>
                          <CustomArea
                            placeholder="Enter add more information"
                            value={formData.addMoreInfo}
                            onChange={(e) =>
                              handleChange("addMoreInfo", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="users"
                            className="text-[14px] font-medium"
                          >
                            How many users were impacted?
                          </label>
                          <CustomInput
                            placeholder="Enter number of users"
                            type="text"
                            value={formData.usersImpacted}
                            onChange={(e) =>
                              handleChange("usersImpacted", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="member"
                            className="text-[14px] font-medium"
                          >
                            What is the member ID?
                          </label>
                          <CustomInput
                            placeholder="Enter member"
                            type="text"
                            value={formData.memberId}
                            onChange={(e) =>
                              handleChange("memberId", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex flex-col items-start">
                          <label
                            htmlFor="replicate"
                            className="text-[14px] font-medium"
                          >
                            Were you able to replicate the issue?
                          </label>
                          <CustomSelect
                            placeholder="Were you able to replicate the issue?"
                            options={["Yes", "No"]}
                            value={formData.replicated}
                            onChange={(e) =>
                              handleChange("replicated", e.target.value)
                            }
                          />
                        </div>

                        <div className="w-full mb-[15px] flex items-start justify-between">
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="date"
                              className="text-[14px] font-medium"
                            >
                              Date
                            </label>
                            <CustomInput
                              type="date"
                              value={formData.date}
                              onChange={(e) =>
                                handleChange("date", e.target.value)
                              }
                            />
                          </div>
                          <div className="w-[49%] flex flex-col items-start">
                            <label
                              htmlFor="time"
                              className="text-[14px] font-medium"
                            >
                              Time
                            </label>
                            <CustomInput
                              type="time"
                              value={formData.time}
                              onChange={(e) =>
                                handleChange("time", e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="mb-[15px] w-full">
                          <p className="text-[14px] font-medium">
                            Add a screenshot or file
                          </p>
                          <div className="w-full h-[130px] bg-[#EAEAEA] p-[15px] rounded flex flex-col items-center justify-center">
                            <input
                              type="file"
                              multiple
                              onChange={handleFileChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-[5%] w-full">
                    <button
                      disabled={isLoading}
                      onClick={submitSupportTicket}
                      className="bg-[#000000] text-white text-[15px] font-bold rounded-sm w-full h-[40px] flex gap-[5px] items-center justify-center"
                    >
                      {isLoading && <Loading />}
                      Submit
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default HomeLayout;
